import * as Sentry from '@sentry/react';
import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useCurrentSession, type CurrentSession } from '../hooks/queries/useCurrentSession';
import { PATHS } from '../paths';

export type LoggedInUser = NonNullable<NonNullable<ReturnType<typeof useCurrentSession>['session']>['user']>;

interface IAuthContextValue {
  isAuthenticated: boolean;
  user: LoggedInUser | null;
  session: CurrentSession | null;
  loading: boolean;
}

const defaultValues: IAuthContextValue = {
  isAuthenticated: false,
  user: null,
  session: null,
  loading: true,
};

const AuthContext = React.createContext<IAuthContextValue>(defaultValues);

type IAuthProviderProps = React.PropsWithChildren;

const AuthProvider = ({ children }: IAuthProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading, refetch } = useCurrentSession();
  const hasHandledInitialRouteRefetch = React.useRef(false);

  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    if (!loading) {
      if (session) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        if (location.pathname !== PATHS.LOGIN) {
          void navigate(PATHS.LOGIN);
        }
      }
    }
  }, [session, loading, navigate, location.pathname]);

  React.useEffect(() => {
    if (!hasHandledInitialRouteRefetch.current) {
      hasHandledInitialRouteRefetch.current = true;
      return;
    }

    if (location.pathname === PATHS.LOGIN) {
      return;
    }
    void refetch();
  }, [location.pathname, refetch]);

  React.useEffect(() => {
    const handleWindowFocus = () => {
      if (window.location.pathname !== PATHS.LOGIN) {
        void refetch();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [refetch]);

  // Set Sentry user context when user is available
  React.useEffect(() => {
    if (session?.user) {
      Sentry.setUser({
        id: session.user.id,
        email: session.user.email ?? undefined,
      });
    } else {
      // Clear user context when logged out
      Sentry.setUser(null);
    }
  }, [session?.user]);

  if (loading) {
    return null;
  }

  const contextValue: IAuthContextValue = {
    isAuthenticated,
    user: session?.user ?? null,
    session: session ?? null,
    loading,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuthState = () => React.useContext(AuthContext);

export default AuthProvider;
